import { Box, Typography, useTheme } from "@mui/material";
import { TooltipTrigger } from "components/Tooltip/styled";
import { useTooltips } from "hooks/useTooltips";
import { Card, Col, Row } from "v2/components/base/Flex/ui";
import { MainPageLayout } from "v2/layouts/MainPageLayout";
import HelpIcon from "@mui/icons-material/Help";
import { DiscoverLayout } from "v2/layouts/DiscoverLayout";
import { ArrowDropDown, Close, Search } from "@mui/icons-material";
import BasicButton from "v2/components/Buttons/BasicButton";
import Scrollable from "v2/components/base/Scrollable/ui";
import { useEffect, useRef, useState } from "react";
import {
  DNAAttribute,
  SCORE_LEVEL,
  MAX_TRAIT_SCORE,
  DNA_CLASS_CONFIG_MAP,
  DNA_ATTRIBUTE_CONFIG_MAP,
} from "pages/cabal-id/dna/dnaConfig";
import useUserStoreV2 from "store/user-store-v2/useUserStoreV2";
import { DNAClass } from "pages/cabal-id/dna/dnaConfig";
import { useInfiniteQuery } from "@tanstack/react-query";
import axiosService from "services/axios";
import { useInViewInfiniteQuery } from "hooks/useInViewInfiniteQuery";
import { useLocation } from "react-router-dom";
import GroupRowItem from "v2/components/ScrollableItem/GroupRowItem/ui";
import { Cabal } from "types";
import FilterByClassModal from "v2/components/Models/FilterByClassModal";
import { FilterByTraitModal } from "v2/components/Models/FilterByTraitModal";
import Select from "v2/components/base/Select/ui";
import SearchDrawer from "v2/components/SearchDrawer/ui";
import DefinitionTooltip from "v2/components/Tooltip/ui";
import { useCheckViewPoint } from "hooks/useCheckViewPoint";
import { VIEW_POINT_CONFIG } from "hooks/useCheckViewPoint";

export const DEFAULT_TRAITS: [DNAAttribute, number[]][] = [
  ["accuracyScore", [0, 5]],
  ["pumpScore", [0, 5]],
  ["socialScore", [0, 5]],
  ["earlyScore", [0, 5]],
  ["insightScore", [0, 5]],
];

const searchFilterDrawerConfig = {
  key: "groupSearchFilter",
  placeholder: "Search Community",
};

const languageSelectOptions = [
  { label: "Languages", value: "all" },
  {
    label: "English",
    value: "en",
  },
  {
    label: "한국어",
    value: "ko",
  },
  {
    label: "中文",
    value: "zh",
  },
  // {
  //   label: "русский",
  //   value: "ru",
  // },
  {
    label: "Tiếng Việt",
    value: "vi",
  },
];

export const GroupPage = () => {
  const theme = useTheme();

  /* Search Drawer */
  const [isShowSearchDrawer, setIsShowSearchDrawer] = useState(false);

  /* User */
  const { tgUserId, walletAddresses = {} } = useUserStoreV2();
  const { tonAddress } = walletAddresses;
  const [page, setPage] = useState(1);

  /* Filter Modal */
  const [isShowFilterByClassModal, setIsShowFilterByClassModal] = useState(false);
  const [isShowFilterByTraitModal, setIsShowFilterByTraitModal] = useState(false);

  /* Filters */
  const [selectedClasses, setSelectedClasses] = useState<Map<DNAClass, boolean>>(
    localStorage.getItem("selectedClasses")
      ? new Map(JSON.parse(localStorage.getItem("selectedClasses")!))
      : new Map(),
  );
  const [selectedTraits, setSelectedTraits] = useState<Map<DNAAttribute, number[]>>(
    localStorage.getItem("selectedTraits")
      ? new Map(JSON.parse(localStorage.getItem("selectedTraits")!))
      : new Map(DEFAULT_TRAITS),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage")
      ? localStorage.getItem("selectedLanguage")
      : languageSelectOptions[0].value,
  );

  /* Search Filter */
  const [searchFilter, setSearchFilter] = useState(
    localStorage.getItem(searchFilterDrawerConfig.key),
  );

  /* Data */
  const queryResult = useInfiniteQuery({
    queryKey: [
      "tg-groups",
      {
        page,
        language: selectedLanguage ?? "all",
        name: "",
        groupType: "all",
        pricing: "all",
        mode: "all",
        ...(selectedClasses.size > 0
          ? { classNames: Array.from(selectedClasses.keys()).join(",") }
          : {}),
        ...(selectedTraits.size > 0
          ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
              const value = selectedTraits.get(key as DNAAttribute);
              if (value) {
                const min =
                  value[0] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[0]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                const max =
                  value[1] === 5
                    ? MAX_TRAIT_SCORE
                    : SCORE_LEVEL[
                        ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                          value[1]
                        ] as keyof typeof SCORE_LEVEL
                      ]!.toString();
                acc[key] = `${min}:${max}`;
              }
              return acc;
            }, {} as Record<DNAAttribute, string>)
          : {}),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTelegramGroups({
        filter: {
          pricing: "all",
          language: selectedLanguage ?? "all",
          groupType: "all",
          name: "",
          tgUserId: tgUserId?.toString(),
          tonAddress,
          ...(selectedClasses.size > 0
            ? { classNames: Array.from(selectedClasses.keys()).join(",") }
            : {}),
          ...(selectedTraits.size > 0
            ? Array.from(selectedTraits.keys()).reduce((acc, key) => {
                const value = selectedTraits.get(key as DNAAttribute);
                if (value) {
                  const min =
                    value[0] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[0]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  const max =
                    value[1] === 5
                      ? MAX_TRAIT_SCORE
                      : SCORE_LEVEL[
                          ["VERY_LOW", "LOW", "MEDIUM", "HIGH", "VERY_HIGH"][
                            value[1]
                          ] as keyof typeof SCORE_LEVEL
                        ]!.toString();
                  acc[key] = `${min}:${max}`;
                }
                return acc;
              }, {} as Record<DNAAttribute, string>)
            : {}),
        },
        selects: [],
        limit: 10,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });
  const { data, isLoading, isSuccess } = queryResult;

  const [pageBySearch, setPageBySearch] = useState(1);
  const queryResultBySearch = useInfiniteQuery({
    queryKey: [
      "tg-groups-search",
      {
        page: pageBySearch,
        language: "all",
        name: "",
        groupType: "all",
        pricing: "all",
        mode: "all",
        ...(searchFilter ? { search: searchFilter } : {}),
        ...Array.from(selectedTraits.keys()).reduce((acc, key) => {
          const min = 0;
          const max = MAX_TRAIT_SCORE;
          acc[key] = `${min}:${max}`;

          return acc;
        }, {} as Record<DNAAttribute, string>),
      },
    ],
    queryFn: async ({ pageParam }) => {
      return axiosService.getTelegramGroups({
        filter: {
          pricing: "all",
          language: "all",
          groupType: "all",
          name: "",
          tgUserId: tgUserId?.toString(),
          tonAddress,
          ...(searchFilter ? { search: searchFilter } : {}),
          ...Array.from(selectedTraits.keys()).reduce((acc, key) => {
            const min = 0;
            const max = MAX_TRAIT_SCORE;
            acc[key] = `${min}:${max}`;

            return acc;
          }, {} as Record<DNAAttribute, string>),
        },
        selects: [],
        limit: 10,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.page + 1;
    },

    getPreviousPageParam: (firstPage, pages) => {
      return firstPage.page - 1;
    },
    initialPageParam: 1,
  });
  const {
    data: dataBySearch,
    isLoading: isLoadingBySearch,
    isSuccess: isSuccessBySearch,
  } = queryResultBySearch;

  const { ref, hasAllFetched } = useInViewInfiniteQuery(queryResult);
  const { ref: refBySearch, hasAllFetched: hasAllFetchedBySearch } =
    useInViewInfiniteQuery(queryResultBySearch);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isShowBuyNowModal = search ? !!searchParams.get("isShowListNowModal") : false;

  useEffect(() => {
    if (window.localStorage.getItem("leaderboardHeight") !== undefined) {
      const scrollHeight = window.localStorage.getItem("leaderboardHeight");

      if (scrollHeight) {
        window.localStorage.removeItem("leaderboardHeight");
        scrollableRef.current?.scrollTo(0, scrollHeight ? parseInt(scrollHeight) : 0);
      }
    }
  }, [data]);

  /* Handlers */
  const handleFilterByClassModalOpen = () => {
    if (selectedClasses.size > 0) {
      setSelectedClasses(new Map());
      localStorage.removeItem("selectedClasses");
    } else {
      setIsShowFilterByClassModal(true);
    }
  };

  const handleFilterByTraitModalOpen = () => {
    if ([...selectedTraits.values()]?.some((value) => value[0] !== 0 || value[1] !== 5)) {
      setSelectedTraits(new Map(DEFAULT_TRAITS));
      localStorage.removeItem("selectedTraits");
    } else {
      setIsShowFilterByTraitModal(true);
    }
  };

  const handleCabalClick = () => {
    if (scrollableRef.current) {
      localStorage.setItem("leaderboardHeight", scrollableRef.current.scrollTop.toString());
    }
  };

  const handleLanguageChange = (event: any) => {
    localStorage.setItem("selectedLanguage", event.target.value as string);
    setSelectedLanguage(event.target.value as string);
  };

  const handleOpenSearchDrawer = () => {
    setIsShowSearchDrawer(true);
  };

  const handleCloseSearchDrawer = () => {
    setIsShowSearchDrawer(false);
  };

  const handleSearch = (value: string) => {
    setSearchFilter(value);
  };

  return (
    <>
      <MainPageLayout
        Title={
          <Header
            title="Discover Smart Community"
            tooltipText="Top communities based on SHILL score - a measurement of influence that is awarded through
          a blend of attention, alpha quality and effort"
          />
        }>
        <DiscoverLayout
          Filters={
            <>
              <BasicButton
                variant="black"
                sx={{ minWidth: "48px", height: "45px" }}
                onClick={handleOpenSearchDrawer}>
                <Search sx={{ width: "16px", height: "16px" }} />
              </BasicButton>
              <FilterItem
                title="Classes"
                selectedUI={<ClassFilterSelectedUI selectedClasses={selectedClasses} />}
                isSelected={!selectedClasses.size}
                onClick={handleFilterByClassModalOpen}
              />
              <FilterItem
                title="Traits"
                selectedUI={<TraitFilterSelectedUI selectedTraits={selectedTraits} />}
                isSelected={[...selectedTraits.values()]?.every(
                  (value) => value[0] === 0 && value[1] === 5,
                )}
                onClick={handleFilterByTraitModalOpen}
              />
              <Select
                sx={{ width: "fit-content", height: "45px" }}
                defaultValue={languageSelectOptions[0].value}
                value={selectedLanguage}
                onChange={handleLanguageChange}
                options={languageSelectOptions}
              />
            </>
          }
          Scrollable={
            <Scrollable
              ref={scrollableRef}
              isLoading={isLoading}
              isEmpty={data?.pages[0].totalResults === 0}
              hasAllFetched={hasAllFetched}
              loadMoreRef={ref}>
              {data?.pages
                .flatMap((page) => page.results)
                .map((group: any, index: number) => {
                  return (
                    <Row key={group._id} fullWidth>
                      <GroupRowItem
                        cabal={group as Cabal}
                        rank={index + 1}
                        onClick={handleCabalClick}
                      />
                    </Row>
                  );
                })}
            </Scrollable>
          }
        />
      </MainPageLayout>
      <FilterByClassModal
        open={isShowFilterByClassModal}
        onClose={() => setIsShowFilterByClassModal(false)}
        selectedClasses={selectedClasses}
        setSelectedClasses={setSelectedClasses}
      />
      <FilterByTraitModal
        open={isShowFilterByTraitModal}
        onClose={() => setIsShowFilterByTraitModal(false)}
        selectedTraits={selectedTraits}
        setSelectedTraits={setSelectedTraits}
      />
      <SearchDrawer
        isOpenned={isShowSearchDrawer}
        data={dataBySearch?.pages.flatMap((page) => page.results) || []}
        isLoadedData={isSuccessBySearch}
        isLoadingData={isLoadingBySearch}
        cachedKey={searchFilterDrawerConfig.key}
        placeholder={searchFilterDrawerConfig.placeholder}
        isEmpty={dataBySearch?.pages[0].totalResults === 0}
        hasAllFetched={hasAllFetchedBySearch}
        loadMoreRef={refBySearch}
        close={handleCloseSearchDrawer}
        onSearch={handleSearch}>
        {dataBySearch?.pages
          .flatMap((page) => page.results)
          .map((group: any, index: number) => {
            return (
              <Box sx={{ width: "100%" }} key={group._id}>
                <GroupRowItem cabal={group as Cabal} rank={index + 4} onClick={handleCabalClick} />
              </Box>
            );
          })}
      </SearchDrawer>
    </>
  );
};

const Header = ({ title, tooltipText }: { title: string; tooltipText: string }) => {
  const { isTooltipOpen, setIsTooltipOpen, tooltipRef } = useTooltips();

  const isMobile = useCheckViewPoint(VIEW_POINT_CONFIG.MOBILE);

  const handleTriggerClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  return (
    <>
      {/* Header */}
      <Row type="sc" sx={{ gap: "4px", whiteSpace: "nowrap" }}>
        <Typography variant={isMobile ? "mobile-h3" : "mobile-h2"}>{title}</Typography>
        <TooltipTrigger ref={tooltipRef} isOpen={isTooltipOpen} onClick={handleTriggerClick}>
          <Row sx={{ position: "absolute", top: -14, left: "50%" }}>
            <HelpIcon sx={{ width: "28px", height: "28px" }} />
          </Row>
        </TooltipTrigger>
      </Row>
      {/* Tooltip Modal */}
      <DefinitionTooltip term={title} isShow={isTooltipOpen} maxWidth="480px">
        <Typography variant="body-default">{tooltipText}</Typography>
      </DefinitionTooltip>
    </>
  );
};

const FilterItem = ({
  title,
  selectedUI,
  isSelected,
  onClick,
}: {
  title: string;
  selectedUI: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  return (
    <BasicButton
      variant="dark"
      onClick={onClick}
      sx={{
        minWidth: "fit-content",
        width: "fit-content",
        height: "45px",
        border: !isSelected ? `1px solid ${theme.palette.border.light}` : "none",
      }}>
      <Col type="cc">
        {isSelected ? (
          <Row type="cc">
            <Col type="cc">
              <Typography
                variant="body-default"
                color="text.primary"
                sx={{ textTransform: "capitalize" }}>
                {title.toLowerCase()}
              </Typography>
            </Col>
            <Col type="cc">
              <ArrowDropDown />
            </Col>
          </Row>
        ) : (
          selectedUI
        )}
      </Col>
    </BasicButton>
  );
};

const ClassFilterSelectedUI = ({
  selectedClasses,
}: {
  selectedClasses: Map<DNAClass, boolean>;
}) => {
  return (
    <Row type="cc" sx={{ padding: "2px 0px", gap: "4px" }}>
      <Col type="cc">
        <Typography variant="mobile-action-s" color="text.primary" sx={{ whiteSpace: "nowrap" }}>
          {`${DNA_CLASS_CONFIG_MAP[Array.from(selectedClasses.keys())[0]]?.name} ${
            selectedClasses.size > 1 ? `+${selectedClasses.size - 1}` : ""
          }`}
        </Typography>
      </Col>
      <Row type="cc">
        <Close sx={{ width: "16px", height: "16px" }} />
      </Row>
    </Row>
  );
};

const TraitFilterSelectedUI = ({
  selectedTraits,
}: {
  selectedTraits: Map<DNAAttribute, number[]>;
}) => {
  const theme = useTheme();
  return (
    <Row type="cc" sx={{ padding: "2px 0px", gap: "4px" }}>
      <Col type="cc">
        <Typography variant="mobile-action-s" color="text.primary" sx={{ whiteSpace: "nowrap" }}>
          {`${
            DNA_ATTRIBUTE_CONFIG_MAP[
              Array.from(selectedTraits.entries()).filter(
                ([key, value]) => value[0] !== 0 || value[1] !== 5,
              )[0][0]
            ]?.name
          } ${
            [...selectedTraits.values()]?.filter((value) => value[0] !== 0 || value[1] !== 5)
              .length > 1
              ? `+${
                  [...selectedTraits.values()]?.filter((value) => value[0] !== 0 || value[1] !== 5)
                    .length - 1
                }`
              : ""
          }`}
        </Typography>
      </Col>
      <Row type="cc">
        <Close sx={{ width: "16px", height: "16px" }} />
      </Row>
    </Row>
  );
};
